"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { collection, getDocs, query, orderBy, limit, doc, getDoc } from "firebase/firestore"
import { onAuthStateChanged } from "firebase/auth"
import { db, auth } from "../firebase"
import {
  FaCalendarAlt,
  FaImages,
  FaUsers,
  FaBuilding,
  FaUserCheck,
  FaChartLine,
  FaArrowUp,
  FaClock,
  FaCheckCircle,
  FaPlus,
  FaUpload,
  FaEye,
  FaSync,
  FaBell,
} from "react-icons/fa"
import { MdDashboard } from "react-icons/md"
import AdminNotifications from "./AdminNotifications"

const AdminUpdateForm = () => {
  const [user, setUser] = useState(null)
  const [selectedOption, setSelectedOption] = useState("")
  const [showNotifications, setShowNotifications] = useState(false)
  const [stats, setStats] = useState({
    totalEvents: 0,
    activeMembers: 0,
    galleryPhotos: 0,
    departments: 15,
  })
  const [recentActivity, setRecentActivity] = useState([])
  const [loading, setLoading] = useState(true)
  const [approvedAdmins, setApprovedAdmins] = useState([])
  const navigate = useNavigate()

  // Hardcoded admin emails
  const hardcodedAdmins = [
    "sivapratap2004@gmail.com",
    "iiche@cb.amrita.edu",
    "ananyasai.paruchuri@gmail.com",
    "cb.en.u4che22033@cb.students.amrita.edu",
    "sahithiuppari469@gmail.com",
  ]

  // Check if user is admin (either hardcoded or approved)
  const isAdmin = (userEmail) => {
    return hardcodedAdmins.includes(userEmail) || approvedAdmins.includes(userEmail)
  }

  const fetchApprovedAdmins = async () => {
    try {
      const approvedAdminsDoc = await getDoc(doc(db, "settings", "approvedAdmins"))
      if (approvedAdminsDoc.exists()) {
        const data = approvedAdminsDoc.data()
        setApprovedAdmins(data.emails || [])
      }
    } catch (error) {
      console.error("Error fetching approved admins:", error)
    }
  }

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser)
      if (currentUser?.email) {
        // Fetch approved admins first
        await fetchApprovedAdmins()

        // Then check if user is admin and fetch dashboard data
        if (hardcodedAdmins.includes(currentUser.email)) {
          fetchDashboardData()
        } else {
          // Check if user is in approved admins
          const approvedAdminsDoc = await getDoc(doc(db, "settings", "approvedAdmins"))
          if (approvedAdminsDoc.exists()) {
            const data = approvedAdminsDoc.data()
            const approvedEmails = data.emails || []
            if (approvedEmails.includes(currentUser.email)) {
              fetchDashboardData()
            } else {
              setLoading(false)
            }
          } else {
            setLoading(false)
          }
        }
      } else {
        setLoading(false)
      }
    })

    return () => unsubscribeAuth()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)

      // Fetch events count
      const eventsSnapshot = await getDocs(collection(db, "events"))
      const totalEvents = eventsSnapshot.size

      // Fetch committee members count
      const committeesCollections = [
        "publicRelations",
        "socialEnvironmental",
        "technical",
        "alumniIndustryConnect",
        "executiveCommittee",
      ]

      let totalMembers = 0
      for (const collectionName of committeesCollections) {
        try {
          const snapshot = await getDocs(collection(db, collectionName))
          totalMembers += snapshot.size
        } catch (error) {
          console.log(`Collection ${collectionName} might not exist yet`)
        }
      }

      // Fetch images count
      let galleryCount = 248
      try {
        const imagesSnapshot = await getDocs(collection(db, "images"))
        galleryCount = imagesSnapshot.size || 248
      } catch (error) {
        console.log("Images collection might not exist yet")
      }

      // Fetch recent activity from events
      const recentEventsQuery = query(collection(db, "events"), orderBy("createdAt", "desc"), limit(4))
      const recentEventsSnapshot = await getDocs(recentEventsQuery)
      const activities = []

      recentEventsSnapshot.forEach((doc) => {
        const data = doc.data()
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date()
        const timeAgo = getTimeAgo(createdAt)

        activities.push({
          action: "Event created",
          details: data.title || "New event",
          time: timeAgo,
          status: "success",
        })
      })

      if (activities.length === 0) {
        activities.push(
          {
            action: "System initialized",
            details: "Admin dashboard setup completed",
            time: "Just now",
            status: "success",
          },
          {
            action: "Database connected",
            details: "Firebase connection established",
            time: "1 minute ago",
            status: "info",
          },
        )
      }

      setStats({
        totalEvents,
        activeMembers: totalMembers,
        galleryPhotos: galleryCount,
        departments: 15,
      })

      setRecentActivity(activities)
    } catch (error) {
      console.error("Error fetching dashboard data:", error)
      setStats({
        totalEvents: 12,
        activeMembers: 156,
        galleryPhotos: 248,
        departments: 15,
      })

      setRecentActivity([
        {
          action: "Dashboard loaded",
          details: "Using fallback data",
          time: "Just now",
          status: "info",
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const getTimeAgo = (date) => {
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)

    if (diffInSeconds < 60) return "Just now"
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    return `${Math.floor(diffInSeconds / 86400)} days ago`
  }

  const adminOptions = [
    {
      title: "Events",
      description: "Manage upcoming events and activities",
      icon: FaCalendarAlt,
      href: "/admin/events",
      color: "bg-blue-500",
      stats: `${stats.totalEvents} Active`,
    },
    {
      title: "Images",
      description: "Upload and manage gallery images",
      icon: FaImages,
      href: "/admin/images",
      color: "bg-purple-500",
      stats: `${stats.galleryPhotos} Photos`,
    },
    {
      title: "Department",
      description: "Update department information",
      icon: FaBuilding,
      href: "/admin/department",
      color: "bg-green-500",
      stats: `${stats.departments} Departments`,
    },
    {
      title: "Executive Committee",
      description: "Manage EC member details",
      icon: FaUserCheck,
      href: "/admin/ECupdate",
      color: "bg-orange-500",
      stats: "EC Members",
    },
    {
      title: "Committee",
      description: "Update committee structure",
      icon: FaUsers,
      href: "/admin/committee",
      color: "bg-red-500",
      stats: `${stats.activeMembers} Members`,
    },
  ]

  const quickStats = [
    {
      title: "Total Events",
      value: stats.totalEvents.toString(),
      change: "+12%",
      icon: FaCalendarAlt,
      color: "text-blue-600",
    },
    {
      title: "Active Members",
      value: stats.activeMembers.toString(),
      change: "+8%",
      icon: FaUsers,
      color: "text-green-600",
    },
    {
      title: "Gallery Photos",
      value: stats.galleryPhotos.toString(),
      change: "+24%",
      icon: FaImages,
      color: "text-purple-600",
    },
    {
      title: "Departments",
      value: stats.departments.toString(),
      change: "0%",
      icon: FaBuilding,
      color: "text-orange-600",
    },
  ]

  const handleOptionClick = (option) => {
    setSelectedOption(option.title)
    navigate(option.href)
  }

  const handleQuickAction = (path) => {
    navigate(path)
  }

  const handleRefresh = () => {
    fetchDashboardData()
  }

  // Check if user is admin using the combined check
  if (!user?.email || !isAdmin(user.email)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 w-full max-w-md">
          <div className="text-center">
            <FaUserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Access Restricted</h2>
            <p className="text-gray-600 dark:text-gray-400">You don't have permission to access the admin dashboard.</p>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="text-center flex-1">
              <div className="flex items-center justify-center mb-4">
                <MdDashboard className="h-8 w-8 text-green-600 mr-3" />
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
                  <span className="text-green-600">IIChE</span> Admin Dashboard
                </h1>
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">
                Indian Institute of Chemical Engineers - Amrita Student Chapter
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 relative"
              >
                <FaBell className="h-4 w-4" />
                <span className="hidden sm:block">Notifications</span>
              </button>
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200"
              >
                <FaSync className="h-4 w-4" />
                <span className="hidden sm:block">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Notifications Panel */}
        {showNotifications && (
          <div className="mb-8">
            <AdminNotifications />
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {quickStats.map((stat, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                  <p className={`text-sm ${stat.change.startsWith("+") ? "text-green-600" : "text-gray-500"}`}>
                    {stat.change} from last month
                  </p>
                </div>
                <div className={`p-3 rounded-full bg-gray-100 dark:bg-gray-700 ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Admin Options */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <FaChartLine className="h-5 w-5" />
                  Admin Controls
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">Select a section to manage website content</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {adminOptions.map((option, index) => (
                    <div
                      key={index}
                      onClick={() => handleOptionClick(option)}
                      className="bg-white dark:bg-gray-700 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer group p-6 border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div
                          className={`p-3 rounded-lg ${option.color} text-white group-hover:scale-110 transition-transform duration-300`}
                        >
                          <option.icon className="h-6 w-6" />
                        </div>
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-full">
                          {option.stats}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{option.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{option.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-6">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <FaClock className="h-5 w-5" />
                  Recent Activity
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">Latest updates and changes</p>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
                    >
                      <div
                        className={`p-1 rounded-full ${
                          activity.status === "success"
                            ? "bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400"
                            : "bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400"
                        }`}
                      >
                        <FaCheckCircle className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{activity.action}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{activity.details}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleRefresh}
                  className="w-full mt-4 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  Refresh Activity
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <FaArrowUp className="h-5 w-5" />
                  Quick Actions
                </h2>
              </div>
              <div className="p-6 space-y-3">
                <button
                  onClick={() => handleQuickAction("/admin/events")}
                  className="w-full flex items-center justify-start px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  <FaPlus className="h-4 w-4 mr-2" />
                  Create New Event
                </button>
                <button
                  onClick={() => handleQuickAction("/admin/committee")}
                  className="w-full flex items-center justify-start px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  <FaUserCheck className="h-4 w-4 mr-2" />
                  Add Committee Member
                </button>
                <button
                  onClick={() => handleQuickAction("/admin/images")}
                  className="w-full flex items-center justify-start px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  <FaUpload className="h-4 w-4 mr-2" />
                  Upload Gallery Images
                </button>
                <button
                  onClick={() => handleQuickAction("/admin/department")}
                  className="w-full flex items-center justify-start px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  <FaEye className="h-4 w-4 mr-2" />
                  View Department
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Selected Option Display */}
        {selectedOption && (
          <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center">
            <p className="text-xl font-semibold text-gray-700 dark:text-white">
              Redirecting to: <span className="text-green-500">{selectedOption}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminUpdateForm
